// VideoEditor.tsx
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Typography,
} from '@mui/material';
import { Icon } from 'components';
import { TextField } from 'components/textField';
import { theme } from 'config';
import { observer } from 'mobx-react';
import { Course, Video } from 'models';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { VideoPlayer } from './../course/videoCourse';
import { AddThumbnail } from './addThumbnail';
import { StepEditor } from './stepEditor';
import InputMask, { Props as InputMaskProps } from 'react-input-mask';
import { TextFieldProps } from '@mui/material';

// @ts-ignore
interface MaskedTextFieldProps extends Omit<TextFieldProps, 'inputRef'>, InputMaskProps {}

/**
 * A reusable Masked TextField component using react-input-mask and Material-UI's TextField.
 * It ensures proper TypeScript typing and ref forwarding.
 */
const MaskedTextField = React.forwardRef<HTMLInputElement, MaskedTextFieldProps>(
  ({ mask, children, ...other }, ref) => (
   // @ts-ignore
   <InputMask mask={mask} {...other}>
      {(inputProps: any) => (
        <TextField
          {...inputProps}
          inputRef={ref}
          InputLabelProps={{ shrink: true }}
        />
      )}
    </InputMask>
  )
);

/**
 * Converts a time string in 'hh-mm-ss' format to total seconds.
 * @param timeString - The time string to convert (e.g., '01-15-30').
 * @returns The total number of seconds, or NaN if the format is incorrect.
 */
function timeStringToSeconds(timeString: string): number {
  const parts = timeString.split('-').map(Number);

  if (parts.length !== 3) {
    console.error(`Invalid time format: ${timeString}. Expected format 'hh-mm-ss'.`);
    return NaN;
  }

  const [hours, minutes, seconds] = parts;
  console.log(hours, minutes, seconds);
  
  if ([hours, minutes, seconds].some(isNaN)) {
    console.error(`Invalid numeric values in time string: ${timeString}.`);
    return NaN;
  }

  if (minutes >= 60 || seconds >= 60 || hours < 0 || minutes < 0 || seconds < 0) {
    console.error(`Time values out of range in time string: ${timeString}.`);
    return NaN;
  }

  return (hours * 3600) + (minutes * 60) + seconds;
}

export interface IVideoEditorProps {
  course?: Course;
}

export const VideoEditor = observer((props: IVideoEditorProps) => {
  const { course } = props;
  const { t } = useTranslation();

  const video: Video = course.getCurStep?.video;

  // Initialize state variables as strings in 'hh-mm-ss' format
  const [editTitle, setEditTitle] = React.useState<string>('');
  const [editSecondFrom, setEditSecondFrom] = React.useState<string>('00-00-00');
  const [editSecondTo, setEditSecondTo] = React.useState<string>('00-00-00');

  // Optional: Reset to default if inputs are cleared
  React.useEffect(() => {
    if (!editSecondFrom) setEditSecondFrom('00-00-00');
    if (!editSecondTo) setEditSecondTo('00-00-00');
  }, [editSecondFrom, editSecondTo]);

  if (!video || !course) return null;

  // Convert time strings to seconds for validation and adding chapters
  const secondFromInSeconds = timeStringToSeconds(editSecondFrom);
  const secondToInSeconds = timeStringToSeconds(editSecondTo);

  // Function to validate time format using regex
  const isValidTime = (time: string): boolean => {
    const timePattern = /^([0-1]\d|2[0-3])-[0-5]\d-[0-5]\d$/;
    return timePattern.test(time);
  };

  return (
    <Grid container maxWidth="100rem" margin="auto">
      {/* Title Section */}
      <Grid xs={12} item mb={2}>
        <Typography
          textAlign="center"
          variant="h4"
          marginBottom={theme.spacing(3)}
        >
          {course.getCurStep?.title}
        </Typography>
      </Grid>

      {/* Thumbnail and Vimeo ID Section */}
      <Grid xs={12} item mb={2}>
        {course.getCurStep?.getParent?.isGrid && (
          <AddThumbnail course={course} />
        )}
        <Grid xs={12} item mt={4}>
          <FormControl>
            <FormLabel id="vimeo-video-id-label">
              {t('vimeo_video_id')}
            </FormLabel>
            <TextField
              value={video?.getVimeoId || ''}
              onChange={(e) =>
                video.set('vimeo_id', Number(e.target.value), true)
              }
              id="vimeo-id"
              variant="outlined"
              hiddenLabel
              aria-labelledby="vimeo-video-id-label"
            />
          </FormControl>
        </Grid>
      </Grid>

      {/* Add Chapter Section */}
      <Grid
        xs={12}
        item
        mt={4}
        mb={4}
        sx={{
          opacity: !video || !video?.getVimeoId ? 0.5 : 1,
          pointerEvents: !video || !video?.getVimeoId ? 'none' : 'auto',
        }}
      >
        <Typography component="h1" variant="h5">
          {t('editor_video_add_chapter')}
        </Typography>
        <Grid
          xs={12}
          item
          mt={4}
          flexDirection="row"
          display="flex"
          gap={4}
          alignItems="center"
        >
          {/* Chapter Title */}
          <FormControl>
            <FormLabel id="chapter-title-label">
              {t('common_title')}
            </FormLabel>
            <TextField
              value={editTitle}
              id="title"
              variant="outlined"
              placeholder={'Chapter ' + (video.getChapters.length + 1)}
              hiddenLabel
              aria-labelledby="chapter-title-label"
              onChange={(e) => setEditTitle(e.target.value)}
            />
          </FormControl>

          {/* Chapter Start Time */}
          <FormControl>
            <FormLabel id="chapter-start-time-label">
              {t('editor_video_start')}
            </FormLabel>
            <MaskedTextField
              mask="99-99-99"
              value={editSecondFrom}
              id="second_from"
              variant="outlined"
              placeholder="00-00-00"
              hiddenLabel
              aria-labelledby="chapter-start-time-label"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditSecondFrom(e.target.value)
              }
              error={editSecondFrom !== '' && !isValidTime(editSecondFrom)}
              helperText={
                editSecondFrom !== '' && !isValidTime(editSecondFrom)
                  ? 'Invalid format. Use hh-mm-ss.'
                  : ''
              }
            />
          </FormControl>

          {/* Chapter End Time */}
          <FormControl>
            <FormLabel id="chapter-end-time-label">
              {t('editor_video_end')}
            </FormLabel>
            <MaskedTextField
              mask="99-99-99"
              value={editSecondTo}
              id="second_to"
              variant="outlined"
              placeholder="00-00-01"
              hiddenLabel
              aria-labelledby="chapter-end-time-label"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditSecondTo(e.target.value)
              }
              error={editSecondTo !== '' && !isValidTime(editSecondTo)}
              helperText={
                editSecondTo !== '' && !isValidTime(editSecondTo)
                  ? 'Invalid format. Use hh-mm-ss.'
                  : ''
              }
            />
          </FormControl>

          {/* Add Button */}
          <Box pt={4}>
            <Button
              variant="contained"
              disabled={
                !editSecondTo ||
                !isValidTime(editSecondFrom) ||
                !isValidTime(editSecondTo) ||
                secondFromInSeconds > secondToInSeconds ||
                !editTitle ||
                !!video.getChapters.find(
                  (c) => c.second_from === secondFromInSeconds
                )
              }
              onClick={() => {
                // Add Chapter with converted seconds

                const chapter = {
                  id: Math.random().toString(36).slice(2, 10),
                  second_from: secondFromInSeconds,
                  second_to: secondToInSeconds,
                  title: editTitle,
                };

                console.log('Adding chapter:', chapter);

                video.addChapter(chapter);
                // Reset input fields to default
                setEditSecondFrom('00-00-00');
                setEditSecondTo('00-00-00');
                setEditTitle('');
              }}
            >
              {t('common_add')}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Video Player Section */}
      <Grid xs={12} item mb={2}>
        <VideoPlayer
          key={video?.getVimeoId}
          video={props.course?.getCurStep?.video}
          editable
        />
      </Grid>

      {/* Step Editor Section */}
      <Grid xs={12} item mb={2}>
        <StepEditor course={props.course} noTitle />
      </Grid>
    </Grid>
  );
});
